/**
 * Chrome shared by the three library tabs (`lib-*`), and the pieces their two
 * card species share.
 *
 * Skills, Agents and MCPs each open the same way — a serif masthead, a search
 * bar, a scope row — over three different bodies. These pieces exist because
 * that opening was standing in three files, not as a lobby for everything a
 * tab renders; the same rule `abilities/editorial.tsx` keeps. The card helpers
 * below are here on the same terms: the skill card and the agent card are one
 * species wearing one foot, and a predicate or a formatter kept in only one of
 * them is a pair of twins that drift.
 *
 * Design contract: mockups/library-skills-editorial-2026-08-10.html.
 */

import type { ReactNode } from "react";
import { ChevronDownIcon } from "@/components/icons/ChevronDownIcon";
import { cn } from "@/lib/utils";

/**
 * True if the string is a single renderable glyph: an emoji (presentation or
 * pictographic) or a symbol character (\p{So} — Essentials' manifest icon is
 * '◐', a Geometric Shape that carries no emoji property; without the
 * symbol class it would fall to the letter avatar and draw "E").
 *
 * The zero-width joiner and variation selector are spelled as ESCAPES on
 * purpose: written as literal codepoints they are invisible in the source, and
 * a reformatter or a copy through a normalizing editor drops them silently,
 * quietly narrowing the class with nothing to see in the diff.
 */
export function isEmoji(str: string | undefined): boolean {
  if (!str || str.length > 8) return false;
  return /^[\p{Emoji_Presentation}\p{Extended_Pictographic}\p{So}\u200D\uFE0F]+$/u.test(
    str.trim()
  );
}

/** Install tallies read the same on both cards: 1200 is "1.2k" either side. */
export function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

/** One scope: mine or the library. Gold underlines the live one. */
export function ScopeTab({
  label,
  on,
  onClick,
}: {
  label: string;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        "lib-scope__tab font-sans text-[13px] font-medium text-fg-subtle bg-transparent border-0 border-b-2 border-transparent py-1 px-0.5 cursor-pointer transition-colors hover:text-fg-default",
        on ? "lib-scope__tab--on text-fg-default border-b-gold-ink" : ""
      )}
      aria-current={on ? "true" : undefined}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

/**
 * One chapter of a unified library page: the serif head, the hairline, the
 * mono tally, then whatever body the caller brings — the same grammar the
 * /abilities catalog's CardSection wears, borrowed rather than imported
 * because that one is welded to ability rows. When the caller caps its rows,
 * the one-way "Show N more" expander sits under the body; the caller owns the
 * expanded state because its lazy loading has to know about it.
 */
export function LibrarySection({
  title,
  italicWord,
  count,
  hiddenCount = 0,
  onShowMore,
  children,
}: {
  title: string;
  italicWord?: string;
  count?: number;
  hiddenCount?: number;
  onShowMore?: () => void;
  children: ReactNode;
}) {
  return (
    <section className="ab-sect">
      <div className="ab-sect__head flex items-baseline gap-3 mb-4.5">
        <h2 className="ab-sect__title font-serif font-normal text-7 tracking-[-0.01em] text-fg-default whitespace-nowrap [&_em]:italic max-sm:text-6">
          {title} {italicWord ? <em>{italicWord}</em> : null}
        </h2>
        <span className="ab-sect__rule flex-1 h-px bg-border-subtle transform-[translateY(-6px)]" />
        {count !== undefined && (
          <span className="ab-sect__count font-mono text-3 tracking-[0.08em] text-fg-subtle transform-[translateY(-4px)]">
            {count}
          </span>
        )}
      </div>
      {children}
      {hiddenCount > 0 && onShowMore && (
        <button
          type="button"
          className="ab-sect__more flex items-center justify-center gap-2 w-full mt-4 p-3 bg-none border border-dashed border-border-default rounded-12 font-mono text-3 tracking-[0.06em] text-fg-subtle cursor-pointer transition-colors hover:bg-bg-subtle hover:text-fg-default hover:border-ab-gold-line [&_svg]:w-3 [&_svg]:h-3 [&_svg]:[stroke:currentColor]"
          onClick={onShowMore}
        >
          <span>Show {hiddenCount} more</span>
          <ChevronDownIcon width={12} height={12} />
        </button>
      )}
    </section>
  );
}

/** The quiet dashed-border card the catalog shows when a search finds nothing. */
export function LibraryEmpty({ message }: { message: string }) {
  return (
    <div
      className="flex flex-col items-center justify-center text-center px-6 py-16
        rounded-16 border border-dashed border-border-subtle bg-bg-subtle"
    >
      <p className="text-3.5 text-fg-muted">{message}</p>
    </div>
  );
}

/** A card's byline: one mono line, led by a 16px coin — the real avatar where
 *  there is one, the initial in gold wash where there isn't. */
export function LibraryByline({
  name,
  avatar,
}: {
  name: string;
  avatar?: string;
}) {
  return (
    <span className="lib-card__by inline-flex items-center gap-2 min-w-0 font-mono text-3 tracking-wider text-fg-subtle whitespace-nowrap overflow-hidden text-ellipsis">
      {avatar ? (
        <img
          src={avatar}
          alt=""
          className="lib-card__avatar w-4 h-4 rounded-full shrink-0 inline-flex items-center justify-center [object-fit:cover] font-sans text-2.5 font-semibold text-gold-ink bg-chrome-gold-molten-mid-tint"
          referrerPolicy="no-referrer"
        />
      ) : (
        <span
          className="lib-card__avatar w-4 h-4 rounded-full shrink-0 inline-flex items-center justify-center [object-fit:cover] font-sans text-2.5 font-semibold text-gold-ink bg-chrome-gold-molten-mid-tint"
          aria-hidden
        >
          {name.charAt(0).toUpperCase()}
        </span>
      )}
      by {name}
    </span>
  );
}
